import { useState, useEffect, useRef, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Hexagon, Send, Image as ImageIcon, X, CheckCircle2, AlertCircle, Loader2,
  Facebook, Instagram, Linkedin, Megaphone, Sparkles, Globe2, Target,
  TrendingUp, BarChart3, Lock, Plug, Activity, Clock, IndianRupee, FileSearch
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

// Phase 89A — SocialBee dashboard. Mirrors the screenshot brief: dark
// sidebar (inherited from AppShell), light-pane workspace with:
//   1) Universal Post & Ad Wizard (left/main)
//   2) Targeting matrix + Connected Accounts (right)
//   3) Global Reach donut + Top Performing Ads table (bottom)

const PLATFORM_META = {
  meta_facebook:  { label: 'Facebook',   Icon: Facebook,  color: 'text-blue-600',    ring: 'ring-blue-200',    chart: '#1877F2' },
  meta_instagram: { label: 'Instagram',  Icon: Instagram, color: 'text-pink-600',    ring: 'ring-pink-200',    chart: '#E4405F' },
  linkedin:       { label: 'LinkedIn',   Icon: Linkedin,  color: 'text-sky-700',     ring: 'ring-sky-200',     chart: '#0A66C2' },
};

const CHAR_LIMITS = { meta_facebook: 63206, meta_instagram: 2200, linkedin: 3000 };
const COUNTRY_OPTIONS = [
  { value: 'IN', label: 'India' }, { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' }, { value: 'AE', label: 'UAE' },
  { value: 'AU', label: 'Australia' }, { value: 'CA', label: 'Canada' },
];

export default function SocialBee() {
  const [access, setAccess] = useState(null); // {has_access, tier, upgrade_required}
  const [credentials, setCredentials] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const [campaignType, setCampaignType] = useState('ORGANIC'); // ORGANIC | PAID
  const [title, setTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]); // {file, url}
  const [selectedPlatforms, setSelectedPlatforms] = useState(['meta_facebook']);
  const [country, setCountry] = useState('IN');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [traceCampaign, setTraceCampaign] = useState(null); // Phase 89D — drawer
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get('/socialbee/access').then(r => setAccess(r.data)).catch(() => setAccess({ has_access: false }));
  }, []);

  useEffect(() => {
    if (!access?.has_access) { setLoading(false); return; }
    let alive = true;
    Promise.all([
      api.get('/socialbee/credentials').then(r => r.data),
      api.get('/socialbee/campaigns').then(r => r.data),
      api.get('/socialbee/dashboard').then(r => r.data),
    ]).then(([creds, camp, dash]) => {
      if (!alive) return;
      setCredentials(creds.credentials || []);
      setCampaigns(camp.campaigns || []);
      setDashboard(dash);
    }).catch(() => {
      toast.error('Could not load SocialBee workspace');
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [access]);

  // Compute the dynamic character ceiling = min across selected platforms.
  const charCeiling = useMemo(() => {
    if (!selectedPlatforms.length) return CHAR_LIMITS.meta_facebook;
    return Math.min(...selectedPlatforms.map(p => CHAR_LIMITS[p] ?? 99999));
  }, [selectedPlatforms]);
  const overLimit = bodyText.length > charCeiling;

  const togglePlatform = (p) => {
    setSelectedPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  const onPickFiles = (filelist) => {
    const newOnes = Array.from(filelist || []).slice(0, 4).map(f => ({
      file: f, url: URL.createObjectURL(f), name: f.name, size: f.size, type: f.type,
    }));
    setMediaFiles(prev => [...prev, ...newOnes].slice(0, 4));
  };

  const removeMedia = (idx) => {
    setMediaFiles(prev => {
      const x = [...prev]; URL.revokeObjectURL(x[idx]?.url); x.splice(idx, 1); return x;
    });
  };

  const refreshLists = async () => {
    const [c, d] = await Promise.all([
      api.get('/socialbee/campaigns').then(r => r.data),
      api.get('/socialbee/dashboard').then(r => r.data),
    ]);
    setCampaigns(c.campaigns || []);
    setDashboard(d);
  };

  const handlePublish = async () => {
    if (!bodyText.trim()) { toast.error('Write something to publish'); return; }
    if (overLimit) { toast.error(`Text exceeds the platform ceiling (${charCeiling} chars)`); return; }
    if (!selectedPlatforms.length) { toast.error('Pick at least one platform'); return; }
    setPublishing(true);
    try {
      // Phase 89A: media not yet uploaded to backend (no storage endpoint wired
      // for SocialBee yet). Send filenames only as a placeholder so the worker
      // knows assets were attached. Real upload + Graph media commit lands in 89B.
      const r = await api.post('/socialbee/campaigns', {
        title: title || undefined,
        body_text: bodyText,
        media_urls: mediaFiles.map(m => m.name),
        platforms: selectedPlatforms,
        campaign_type: campaignType,
        targets: {
          geography: [country],
          categories: selectedCategories,
        },
      });
      toast.success('Campaign dispatched! Workers running…');
      setBodyText(''); setTitle(''); setMediaFiles([]);
      // Brief delay so the simulated workers can flip to SUCCESS before we re-fetch.
      setTimeout(refreshLists, 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  // ---------- Render -----------------------------------------------------
  if (loading || access === null) {
    return (
      <div className="p-6 space-y-4 max-w-7xl mx-auto">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-96 col-span-2 rounded-xl" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!access.has_access) {
    return <UpgradeGate tier={access.tier} />;
  }

  const credByPlatform = Object.fromEntries(credentials.map(c => [c.platform, c]));

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-7xl mx-auto" data-testid="socialbee-page">
      {/* Header */}
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 flex items-center justify-center shadow-md">
            <Hexagon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl font-display font-bold leading-tight">SocialBee</h1>
            <p className="text-xs text-muted-foreground">Omnichannel social media & ads — one post, every audience.</p>
          </div>
        </div>
        <Badge className="bg-gradient-to-r from-amber-500 to-pink-500 text-white border-0 text-[10px]">Phase 89A · Preview</Badge>
      </header>

      {/* Main grid: Wizard (left, 2 cols) + Sidebar (right, 1 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ===== Wizard (main) ===== */}
        <Card className="lg:col-span-2" data-testid="socialbee-wizard">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4" /> Universal Post & Ad Wizard</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={campaignType} onValueChange={(v) => setCampaignType(v)}>
              <TabsList className="w-full">
                <TabsTrigger value="ORGANIC" className="flex-1" data-testid="wizard-tab-organic"><Megaphone className="h-3.5 w-3.5 mr-1" /> Create Post (Organic)</TabsTrigger>
                <TabsTrigger value="PAID" className="flex-1" data-testid="wizard-tab-paid"><IndianRupee className="h-3.5 w-3.5 mr-1" /> Launch Ad Campaign (Paid)</TabsTrigger>
              </TabsList>

              <div className="space-y-3 mt-4">
                <Input placeholder="Title (internal label — won't be posted)" value={title} onChange={e => setTitle(e.target.value)} data-testid="wizard-title-input" />

                <div className="relative">
                  <Textarea
                    placeholder={campaignType === 'PAID'
                      ? "Write your ad copy. Short, punchy, with a clear CTA…"
                      : "What's on your mind? Share an update, a win, a recap…"}
                    value={bodyText}
                    onChange={e => setBodyText(e.target.value)}
                    rows={6}
                    className={overLimit ? 'border-red-400 focus-visible:ring-red-400' : ''}
                    data-testid="wizard-body-input"
                  />
                  <div className={`absolute bottom-2 right-2 text-[10px] tabular-nums ${overLimit ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`} data-testid="wizard-char-count">
                    {bodyText.length.toLocaleString()} / {charCeiling.toLocaleString()}
                  </div>
                </div>

                {/* Media drop */}
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); onPickFiles(e.dataTransfer.files); }}
                  className="border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-lg p-3"
                  data-testid="wizard-media-drop"
                >
                  {mediaFiles.length === 0 ? (
                    <button onClick={() => fileInputRef.current?.click()} className="w-full text-center py-4 text-sm text-muted-foreground hover:text-foreground transition-colors">
                      <ImageIcon className="h-5 w-5 mx-auto mb-1 text-muted-foreground/50" />
                      Drag & drop, or <span className="text-primary underline">choose files</span>
                      <p className="text-[10px] mt-0.5">PNG · JPG · WEBP · MP4 · MOV — up to 4 assets</p>
                    </button>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {mediaFiles.map((m, i) => (
                        <div key={i} className="relative aspect-square rounded-md overflow-hidden border bg-slate-50">
                          {m.type.startsWith('video') ? (
                            <video src={m.url} className="w-full h-full object-cover" />
                          ) : (
                            <img src={m.url} alt={m.name} className="w-full h-full object-cover" />
                          )}
                          <button onClick={() => removeMedia(i)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" data-testid={`wizard-media-remove-${i}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {mediaFiles.length < 4 && (
                        <button onClick={() => fileInputRef.current?.click()} className="aspect-square rounded-md border-2 border-dashed border-slate-200 dark:border-slate-800 flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                          <ImageIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" multiple accept="image/png,image/jpeg,image/webp,video/mp4,video/quicktime" className="hidden" onChange={(e) => onPickFiles(e.target.files)} data-testid="wizard-media-input" />
                </div>

                {/* Platform selector */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Target distribution</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(PLATFORM_META).map(([key, m]) => {
                      const cred = credByPlatform[key];
                      const selected = selectedPlatforms.includes(key);
                      const connected = !!cred && cred.status !== 'token_expired';
                      return (
                        <button
                          key={key}
                          onClick={() => togglePlatform(key)}
                          className={`relative p-2 rounded-lg border text-left transition-all ${selected ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border hover:border-primary/40'}`}
                          data-testid={`wizard-platform-${key}`}
                        >
                          <div className="flex items-center gap-2">
                            <m.Icon className={`h-4 w-4 ${m.color}`} />
                            <span className="text-xs font-medium">{m.label}</span>
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                            <span className="text-[9px] text-muted-foreground">{connected ? 'Connected' : 'Not connected'}</span>
                          </div>
                          {selected && (
                            <CheckCircle2 className="absolute top-1.5 right-1.5 h-3.5 w-3.5 text-primary" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Button onClick={handlePublish} disabled={publishing || overLimit || !bodyText.trim() || !selectedPlatforms.length} className="w-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-600 hover:to-pink-600 text-white" data-testid="wizard-publish-btn">
                  {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  {publishing ? 'Dispatching…' : 'Publish Now'}
                </Button>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* ===== Targeting + Connected Accounts (sidebar) ===== */}
        <div className="space-y-4">
          <Card data-testid="socialbee-targeting">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Target className="h-4 w-4" /> Ad Targeting Matrix</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1 inline-flex items-center gap-1"><Globe2 className="h-3 w-3" /> Geography</p>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="h-8 text-xs" data-testid="targeting-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Industry / Category</p>
                <div className="flex flex-wrap gap-1.5" data-testid="targeting-categories">
                  {Object.entries(dashboard?.categories || {}).map(([k, meta]) => {
                    const on = selectedCategories.includes(k);
                    return (
                      <button
                        key={k}
                        onClick={() => setSelectedCategories(prev => on ? prev.filter(x => x !== k) : [...prev, k])}
                        className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground hover:border-primary/40'}`}
                        data-testid={`targeting-category-${k}`}
                      >
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[9px] text-muted-foreground mt-2">Each pick maps to Meta interest IDs and LinkedIn URNs behind the scenes.</p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="socialbee-connected">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Plug className="h-4 w-4" /> Connected Accounts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(PLATFORM_META).map(([key, m]) => {
                const cred = credByPlatform[key];
                const status = !cred ? 'not_connected' : (cred.status === 'token_expired' ? 'token_expired' : 'active');
                const statusUi = status === 'active'
                  ? { label: 'Active', cls: 'bg-emerald-100 text-emerald-700' }
                  : status === 'token_expired'
                    ? { label: 'Token Expired', cls: 'bg-amber-100 text-amber-700' }
                    : { label: 'Action Required', cls: 'bg-slate-100 text-slate-600' };
                return (
                  <div key={key} className="flex items-center justify-between p-2 rounded-md border" data-testid={`connected-row-${key}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <m.Icon className={`h-4 w-4 ${m.color}`} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{m.label}</p>
                        {cred?.account_name && <p className="text-[10px] text-muted-foreground truncate">{cred.account_name}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge className={`text-[9px] border-0 ${statusUi.cls}`}>{statusUi.label}</Badge>
                      {status === 'not_connected' && (
                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => connectInit(key)} data-testid={`connect-btn-${key}`}>Connect</Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===== Lower analytics row ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1" data-testid="socialbee-reach">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Global Reach</CardTitle>
          </CardHeader>
          <CardContent>
            <ReachDonut dashboard={dashboard} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2" data-testid="socialbee-top-ads">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Recent Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <CampaignsTable campaigns={campaigns} onRowClick={setTraceCampaign} />
          </CardContent>
        </Card>
      </div>

      {/* Phase 89D — Campaign trace drawer */}
      <CampaignTraceDrawer
        campaign={traceCampaign}
        onClose={() => setTraceCampaign(null)}
        onJobRefreshed={(refreshedCampaigns) => {
          setCampaigns(refreshedCampaigns);
          // Re-pin the open drawer to the freshly-refreshed campaign doc so the new
          // job state propagates without forcing the operator to reopen the drawer.
          if (traceCampaign) {
            const fresh = refreshedCampaigns.find(c => c.id === traceCampaign.id);
            if (fresh) setTraceCampaign(fresh);
          }
        }}
      />
    </div>
  );

  async function connectInit(platform) {
    try {
      const r = await api.post(`/socialbee/credentials/${platform}/connect-init`);
      if (r.data.ready && r.data.authorize_url) {
        window.location.href = r.data.authorize_url;
      } else {
        toast.info(r.data.message || 'Setup pending');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start connection');
    }
  }
}

function UpgradeGate({ tier }) {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/socialbee/upgrade/pricing').then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const legendPrice = pricing?.tiers?.find(t => t.key === 'legend')?.monthly_inr ?? 14999;

  const handleUpgrade = async () => {
    setBusy(true);
    try {
      const orderResp = await api.post('/socialbee/upgrade/create-order', { tier: 'legend' });
      const order = orderResp.data;

      // Finalize the upgrade after either real Razorpay handler OR mock short-circuit.
      const finalize = async (rzpResp = {}) => {
        try {
          await api.post('/socialbee/upgrade/verify', {
            tier: 'legend',
            razorpay_order_id: rzpResp.razorpay_order_id || order.order_id,
            razorpay_payment_id: rzpResp.razorpay_payment_id || '',
            razorpay_signature: rzpResp.razorpay_signature || '',
          });
          toast.success('Upgraded to Legend! Reloading…');
          setTimeout(() => window.location.reload(), 900);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Upgrade verification failed');
        } finally {
          setBusy(false);
        }
      };

      if (order.mock_mode || !window.Razorpay) {
        await finalize();
        return;
      }

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: 'Out-Play.in',
        description: order.description,
        handler: (resp) => finalize(resp),
        modal: { ondismiss: () => { setBusy(false); toast.info('Payment cancelled'); } },
        theme: { color: '#F59E0B' },
      });
      rzp.on('payment.failed', (r) => { toast.error(`Payment failed: ${r.error?.description || ''}`); setBusy(false); });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start upgrade');
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-8 text-center" data-testid="socialbee-upgrade-gate">
      <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-400 to-pink-500 mx-auto mb-4 flex items-center justify-center shadow-lg">
        <Lock className="h-7 w-7 text-white" />
      </div>
      <h1 className="text-xl font-display font-bold">SocialBee is a Legend-tier feature</h1>
      <p className="text-sm text-muted-foreground mt-2">
        Your academy is on the <span className="font-semibold capitalize">{tier || 'starter'}</span> plan. Upgrade to <span className="font-semibold">Legend</span> to unlock omnichannel social media and ad campaign management — one click publishes to Facebook, Instagram, and LinkedIn at once.
      </p>
      <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-amber-50 to-pink-50 border border-amber-200 inline-flex items-baseline gap-1.5">
        <span className="text-3xl font-display font-bold tabular-nums">₹{legendPrice.toLocaleString()}</span>
        <span className="text-xs text-muted-foreground">/ month</span>
      </div>
      <Button disabled={busy} className="mt-5 bg-gradient-to-r from-amber-500 to-pink-500 text-white hover:from-amber-600 hover:to-pink-600" onClick={handleUpgrade} data-testid="socialbee-upgrade-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
        {busy ? 'Processing…' : 'Upgrade to Legend'}
      </Button>
      <p className="text-[10px] text-muted-foreground mt-2">Cancel anytime · Powered by Razorpay</p>
    </div>
  );
}

function ReachDonut({ dashboard }) {
  // Synthesize per-platform success counts from the rollup. In Phase 89B these
  // become real impression numbers fetched from each platform's insights API.
  const data = useMemo(() => {
    if (!dashboard?.by_platform) return [];
    return Object.entries(dashboard.by_platform).map(([plat, stats]) => ({
      name: PLATFORM_META[plat]?.label || plat,
      value: (stats.SUCCESS || 0) + (stats.PROCESSING || 0),
      color: PLATFORM_META[plat]?.chart || '#94a3b8',
    })).filter(d => d.value > 0);
  }, [dashboard]);

  if (data.length === 0) {
    return (
      <div className="h-48 flex flex-col items-center justify-center text-center" data-testid="socialbee-reach-empty">
        <Activity className="h-8 w-8 text-muted-foreground/30 mb-1" />
        <p className="text-xs text-muted-foreground">Publish your first post to see reach data here.</p>
      </div>
    );
  }
  return (
    <div className="h-48" data-testid="socialbee-reach-chart">
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
            {data.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Pie>
          <Tooltip />
          <Legend iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function CampaignsTable({ campaigns, onRowClick }) {
  if (!campaigns.length) {
    return (
      <div className="py-10 text-center" data-testid="socialbee-campaigns-empty">
        <Clock className="h-8 w-8 mx-auto text-muted-foreground/30 mb-1" />
        <p className="text-sm font-medium">No campaigns yet</p>
        <p className="text-xs text-muted-foreground">Use the wizard above to launch your first one.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr className="border-b">
            <th className="text-left py-2 font-semibold">Campaign</th>
            <th className="text-left py-2 font-semibold">Type</th>
            <th className="text-left py-2 font-semibold">Platforms</th>
            <th className="text-left py-2 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map(c => {
            const successCount = (c.jobs || []).filter(j => j.status === 'SUCCESS').length;
            const failCount = (c.jobs || []).filter(j => j.status === 'FAILED').length;
            const total = (c.jobs || []).length;
            const status = failCount > 0 ? 'partial' : successCount === total && total > 0 ? 'complete' : 'pending';
            const statusCls = status === 'complete' ? 'bg-emerald-100 text-emerald-700' : status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
            return (
              <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" data-testid={`campaign-row-${c.id}`} onClick={() => onRowClick && onRowClick(c)}>
                <td className="py-2">
                  <p className="font-medium truncate max-w-[200px]">{c.title || c.body_text.slice(0, 32) + (c.body_text.length > 32 ? '…' : '')}</p>
                  <p className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                </td>
                <td className="py-2"><Badge variant="outline" className="text-[10px]">{c.campaign_type}</Badge></td>
                <td className="py-2">
                  <div className="flex items-center gap-1">
                    {(c.platforms || []).map(p => {
                      const M = PLATFORM_META[p];
                      return M ? <M.Icon key={p} className={`h-3.5 w-3.5 ${M.color}`} /> : null;
                    })}
                  </div>
                </td>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] border-0 ${statusCls}`}>
                      {status === 'complete' ? `${successCount}/${total} ✓` : status === 'partial' ? `${failCount} failed` : `${successCount}/${total}…`}
                    </Badge>
                    <FileSearch className="h-3 w-3 text-muted-foreground" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Phase 89D — Per-platform HTTP trace drawer. Surfaces the raw request/response
// trace stored by the platform workers in platform_execution_jobs so Legend
// academies can self-diagnose publish failures (token expired, asset rejected,
// targeting invalid, etc.) without pinging support.
// Phase 89D.1 — Adds an inline "Resend to {platform}" CTA that re-fires the
// worker via /api/socialbee/campaigns/{id}/jobs/{platform}/retry.
function CampaignTraceDrawer({ campaign, onClose, onJobRefreshed }) {
  const [activePlatform, setActivePlatform] = useState(null);
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    if (campaign?.jobs?.length) setActivePlatform(campaign.jobs[0].platform);
    else setActivePlatform(null);
  }, [campaign]);

  if (!campaign) return null;
  const jobs = campaign.jobs || [];
  const activeJob = jobs.find(j => j.platform === activePlatform) || jobs[0];

  const STATUS_BADGE = {
    SUCCESS: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    FAILED:  'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    PROCESSING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    PENDING: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400',
  };

  const refreshCampaigns = async () => {
    try {
      const r = await api.get('/socialbee/campaigns');
      if (onJobRefreshed) onJobRefreshed(r.data.campaigns || []);
    } catch (e) {
      // swallow — drawer stays usable even if refresh hiccups
    }
  };

  const handleRetry = async () => {
    if (!activeJob) return;
    setRetrying(true);
    try {
      await api.post(`/socialbee/campaigns/${campaign.id}/jobs/${activeJob.platform}/retry`);
      toast.success(`Retry queued for ${PLATFORM_META[activeJob.platform]?.label || activeJob.platform}. Watching for new trace…`);
      // 3-tick auto-refresh — gives the worker time to flip PENDING → PROCESSING → terminal.
      // Ticks at 1s, 4s, 9s cover the 600ms simulation + 25s IG polling + immediate-failure paths.
      for (const delay of [1000, 3000, 5000]) {
        await new Promise(res => setTimeout(res, delay));
        await refreshCampaigns();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Dialog open={!!campaign} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="campaign-trace-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5" />
            Campaign Trace
            <Badge variant="outline" className="text-[10px]">{campaign.campaign_type}</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Campaign header */}
        <div className="space-y-2 pb-3 border-b">
          <p className="font-medium text-sm">{campaign.title || campaign.body_text?.slice(0, 80) || '(Untitled)'}</p>
          <p className="text-xs text-muted-foreground whitespace-pre-wrap break-words line-clamp-3">{campaign.body_text}</p>
          <p className="text-[11px] text-muted-foreground">
            Dispatched {new Date(campaign.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })} IST
          </p>
        </div>

        {/* Platform tabs */}
        {jobs.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No platform execution rows recorded for this campaign yet.</div>
        ) : (
          <div className="pt-3">
            <div className="flex gap-1 border-b mb-3 overflow-x-auto">
              {jobs.map(j => {
                const M = PLATFORM_META[j.platform];
                const Icon = M?.Icon || Hexagon;
                const active = j.platform === activePlatform;
                return (
                  <button
                    key={j.id}
                    onClick={() => setActivePlatform(j.platform)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs whitespace-nowrap border-b-2 transition-colors ${active ? 'border-primary text-primary font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    data-testid={`trace-tab-${j.platform}`}
                  >
                    <Icon className={`h-3.5 w-3.5 ${M?.color || ''}`} />
                    {M?.label || j.platform}
                    <Badge className={`text-[9px] border-0 ml-1 ${STATUS_BADGE[j.status] || STATUS_BADGE.PENDING}`}>{j.status}</Badge>
                  </button>
                );
              })}
            </div>

            {activeJob && <PlatformTraceBody job={activeJob} />}

            {/* Phase 89D.1 — Single-platform retry CTA. Allowed when the job is
                terminal (SUCCESS or FAILED). PENDING/PROCESSING are locked out
                because the backend rejects re-fires with 409. */}
            {activeJob && (activeJob.status === 'FAILED' || activeJob.status === 'SUCCESS') && (
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border bg-slate-50 dark:bg-slate-950/40 p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold">
                    {activeJob.status === 'FAILED' ? 'Fixed the issue?' : 'Republish this platform?'}
                  </p>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    {activeJob.status === 'FAILED'
                      ? 'Update the token/asset/config and resend without re-typing the campaign.'
                      : 'Re-fires the worker against the same body + media. Useful for republishing after a temporary outage.'}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={handleRetry}
                  disabled={retrying}
                  className={activeJob.status === 'FAILED' ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
                  data-testid={`trace-retry-${activeJob.platform}`}
                >
                  {retrying ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                  {retrying ? 'Resending…' : `Resend to ${PLATFORM_META[activeJob.platform]?.label || activeJob.platform}`}
                </Button>
              </div>
            )}

            {/* Manual retry history — separate from worker attempts[], persists across re-runs. */}
            {activeJob?.retry_history?.length > 0 && (
              <details className="mt-3 rounded-lg border bg-slate-50 dark:bg-slate-950/40 p-2">
                <summary className="cursor-pointer text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Manual Retries ({activeJob.retry_history.length})
                </summary>
                <table className="w-full text-[10px] mt-2 tabular-nums">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left py-1">When</th>
                      <th className="text-left">By</th>
                      <th className="text-left">Was</th>
                      <th className="text-left">Previous error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeJob.retry_history.map((r, i) => (
                      <tr key={i} className="border-t border-slate-200 dark:border-slate-800">
                        <td className="py-1">{r.at ? new Date(r.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : '—'}</td>
                        <td className="truncate max-w-[140px]">{r.by_email || '—'}</td>
                        <td><Badge className={`text-[9px] border-0 ${STATUS_BADGE[r.previous_status] || STATUS_BADGE.PENDING}`}>{r.previous_status || '—'}</Badge></td>
                        <td className="text-rose-600 truncate max-w-[180px]" title={r.previous_error || ''}>{r.previous_error || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PlatformTraceBody({ job }) {
  const attempts = job.attempts || [];
  return (
    <div className="space-y-3 text-xs">
      {/* Top stats row */}
      <div className="grid grid-cols-3 gap-2">
        <StatTile label="Tier" value={job.tier || '—'} />
        <StatTile label="HTTP Status" value={job.response_status ?? '—'} tone={job.response_status && job.response_status >= 400 ? 'rose' : (job.response_status ? 'emerald' : 'slate')} />
        <StatTile label="Attempts" value={attempts.length || '—'} />
      </div>

      {/* Last error (most actionable thing) */}
      {job.last_error && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/20 p-3" data-testid="trace-last-error">
          <p className="font-semibold text-rose-700 dark:text-rose-300 mb-1">Last error</p>
          <pre className="font-mono text-[10px] whitespace-pre-wrap break-all text-rose-700 dark:text-rose-300 leading-snug">{job.last_error}</pre>
        </div>
      )}

      {/* Request */}
      {job.request_url && (
        <details className="rounded-lg border bg-slate-50 dark:bg-slate-950/40 p-2" open>
          <summary className="cursor-pointer text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Request</summary>
          <div className="mt-2 space-y-1.5">
            <p className="font-mono text-[10px] break-all"><span className="text-emerald-600">{job.request_url}</span></p>
            {job.request_body && (
              <pre className="font-mono text-[10px] bg-white/60 dark:bg-slate-900/60 p-2 rounded whitespace-pre-wrap break-all">{JSON.stringify(job.request_body, null, 2)}</pre>
            )}
          </div>
        </details>
      )}

      {/* Response */}
      {job.response_body && (
        <details className="rounded-lg border bg-slate-50 dark:bg-slate-950/40 p-2" open>
          <summary className="cursor-pointer text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Response</summary>
          <pre className="font-mono text-[10px] mt-2 bg-white/60 dark:bg-slate-900/60 p-2 rounded whitespace-pre-wrap break-all max-h-60 overflow-auto">{JSON.stringify(job.response_body, null, 2)}</pre>
        </details>
      )}

      {/* Attempts timeline */}
      {attempts.length > 0 && (
        <details className="rounded-lg border bg-slate-50 dark:bg-slate-950/40 p-2">
          <summary className="cursor-pointer text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Attempts ({attempts.length})</summary>
          <table className="w-full text-[10px] mt-2 tabular-nums">
            <thead className="text-muted-foreground">
              <tr><th className="text-left py-1">#</th><th className="text-left">When</th><th className="text-left">HTTP</th><th className="text-left">Latency</th><th className="text-left">Error</th></tr>
            </thead>
            <tbody>
              {attempts.map((a, i) => (
                <tr key={i} className="border-t border-slate-200 dark:border-slate-800">
                  <td className="py-1">{a.attempt}</td>
                  <td>{a.at ? new Date(a.at).toLocaleTimeString('en-IN', { hour12: false }) : '—'}</td>
                  <td className={a.status && a.status >= 400 ? 'text-rose-600' : 'text-emerald-600'}>{a.status ?? '—'}</td>
                  <td>{a.latency_ms != null ? `${a.latency_ms}ms` : '—'}</td>
                  <td className="text-rose-600 truncate max-w-[200px]" title={a.error || ''}>{a.error || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function StatTile({ label, value, tone = 'slate' }) {
  const TONE = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    rose: 'text-rose-700 dark:text-rose-300',
    slate: 'text-foreground',
  };
  return (
    <div className="rounded-lg border bg-white dark:bg-slate-950/40 p-2">
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className={`text-sm font-display font-bold tabular-nums ${TONE[tone] || TONE.slate}`}>{value}</p>
    </div>
  );
}

